import { useState, useEffect } from "react";
import { CheckCircle2, Circle, Copy, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_DETAILS = {
  gbp_manager: {
    icon: "📍",
    instructions: [
      "Sign in to your Google Business Profile at business.google.com",
      "Click on the business you want us to manage",
      "Go to Settings → Managers",
      "Click 'Add User' and enter: hello@rankifylocal.com",
      "Set the role to 'Manager' and click 'Invite'",
    ],
    copyText: "hello@rankifylocal.com",
    helpLink: "https://support.google.com/business/answer/3403100",
  },
  confirm_info: {
    icon: "✅",
    instructions: [
      "Review your business name, address, phone number, and hours in your Google Business Profile",
      "Make sure all information matches exactly across your website and all directories",
      "If anything is wrong, update it in Google Business Profile first — we'll sync it everywhere else",
    ],
  },
  review_access: {
    icon: "⭐",
    instructions: [
      "If you use a review management tool (e.g. Birdeye, Podium, NiceJob), share access with hello@rankifylocal.com",
      "If you don't use one yet, we'll set one up for you as part of your plan",
      "This step is optional but helps us automate your review collection faster",
    ],
    copyText: "hello@rankifylocal.com",
  },
  website_access: {
    icon: "🌐",
    instructions: [
      "Add hello@rankifylocal.com as an editor/admin in your website CMS (WordPress, Wix, Squarespace, etc.)",
      "If you use WordPress: go to Users → Add New → enter our email → set role to Editor",
      "This allows us to optimize your title tags, meta descriptions, and on-page content",
      "This step is optional if you prefer to make website changes yourself",
    ],
    copyText: "hello@rankifylocal.com",
  },
};

export default function OnboardingWizard({ placeId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStep, setExpandedStep] = useState(null);
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    if (!placeId) return;
    axios.get(`${API}/onboarding/${placeId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [placeId, token]);

  const toggleStep = async (stepId) => {
    const isCompleted = (data?.completed || []).includes(stepId);
    setCompleting(stepId);
    try {
      const endpoint = isCompleted ? "undo" : "complete";
      await axios.post(`${API}/onboarding/${placeId}/${endpoint}`, { step_id: stepId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(prev => ({
        ...prev,
        completed: isCompleted
          ? (prev.completed || []).filter(s => s !== stepId)
          : [...(prev.completed || []), stepId],
      }));
      toast.success(isCompleted ? "Step unmarked" : "Step completed!");
    } catch {
      toast.error("Failed to update step");
    }
    setCompleting(null);
  };

  if (loading) return null;
  if (!data) return null;

  const steps = data.steps || Object.keys(STEP_DETAILS).map(id => ({ id, title: STEP_DETAILS[id]?.icon + " " + id }));
  const completed = new Set(data.completed || []);
  const progress = steps.length > 0 ? Math.round((completed.size / steps.length) * 100) : 0;

  if (progress === 100) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-5 mb-6" data-testid="onboarding-complete">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          <div>
            <p className="font-bold text-emerald-900 text-sm">Onboarding Complete</p>
            <p className="text-xs text-emerald-600">All setup steps are done. We're now optimizing your presence.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-xl bg-white mb-6 overflow-hidden" data-testid="onboarding-wizard">
      {/* Header */}
      <div className="px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-900 text-sm">Setup Your Account</h3>
          <span className="text-xs font-bold text-indigo-600">{progress}% complete</span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <p className="text-xs text-slate-500 mt-2">Complete these steps so we can start optimizing your local rankings.</p>
      </div>

      {/* Steps */}
      <div className="divide-y">
        {steps.map((step) => {
          const details = STEP_DETAILS[step.id] || {};
          const isDone = completed.has(step.id);
          const isExpanded = expandedStep === step.id;

          return (
            <div key={step.id} data-testid={`onboarding-step-${step.id}`}>
              <div
                className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer transition ${isDone ? "bg-emerald-50/50" : "hover:bg-slate-50"}`}
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
              >
                <button
                  onClick={e => { e.stopPropagation(); toggleStep(step.id); }}
                  className="shrink-0"
                  disabled={completing === step.id}
                  data-testid={`onboarding-check-${step.id}`}
                >
                  {isDone
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    : <Circle className="w-5 h-5 text-slate-300" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDone ? "text-slate-400 line-through" : "text-slate-900"}`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-slate-400">{step.description}</p>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>

              {isExpanded && details.instructions && (
                <div className="px-5 pb-4 bg-slate-50 border-t">
                  <ol className="list-decimal list-inside space-y-2 mt-3">
                    {details.instructions.map((inst, i) => (
                      <li key={i} className="text-sm text-slate-600 leading-relaxed">{inst}</li>
                    ))}
                  </ol>
                  {details.copyText && (
                    <button
                      onClick={() => { navigator.clipboard?.writeText(details.copyText); toast.success("Copied!"); }}
                      className="mt-3 flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      <Copy className="w-3 h-3" /> Copy: {details.copyText}
                    </button>
                  )}
                  {details.helpLink && (
                    <a href={details.helpLink} target="_blank" rel="noopener noreferrer"
                      className="mt-2 flex items-center gap-1.5 text-xs text-slate-400 hover:text-indigo-600">
                      <ExternalLink className="w-3 h-3" /> Google help article
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
